import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Inloggen mislukt');
      }

      login(data.supabaseAccessToken, data.supabaseRefreshToken, data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Server is niet bereikbaar. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#f4f6f9' }}>
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col items-center justify-center p-12"
        style={{ background: 'linear-gradient(135deg, #0d3d6b 0%, #1a6bab 50%, #2389d4 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        <div className="relative z-10 w-full max-w-sm">
          <div className="grid grid-cols-2 gap-3 mb-8 animate-fade-in">
            {[
              'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=300&q=80',
              'https://images.unsplash.com/photo-1499678329028-101435549a4e?w=300&q=80',
              'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=300&q=80',
              'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=300&q=80',
            ].map((src, i) => (
              <div
                key={i}
                className={`rounded-2xl overflow-hidden shadow-xl stagger-${i + 1} animate-fade-in-up`}
                style={{ height: i % 2 === 0 ? 140 : 110, marginTop: i % 2 === 0 ? 0 : 24 }}
              >
                <img
                  src={src}
                  alt="Bestemming"
                  className="w-full h-full object-cover"
                  crossOrigin="anonymous"
                />
              </div>
            ))}
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <span className="text-white font-bold text-xl">TravelAIgent</span>
            </div>
            <p className="text-blue-200 text-sm leading-relaxed">
              Het slimme platform voor onafhankelijke reisagenten.
              <br />
              Meer tijd voor uw klanten, minder voor administratie.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="text-center mb-8">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'var(--brand)' }}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="white"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-900">TravelAIgent</span>
            </div>

            <h1 className="text-2xl font-bold text-gray-900">Welkom terug</h1>
            <p className="text-gray-500 mt-2">Log in op uw reisagent-dashboard</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            {error && (
              <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div>
                <label className="label">E-mailadres</label>
                <input
                  type="email"
                  required
                  className="input"
                  placeholder="jan@reizenaanzee.nl"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>

              <div>
                <label className="label">Wachtwoord</label>
                <input
                  type="password"
                  required
                  className="input"
                  placeholder="Uw wachtwoord"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-3">
                {loading ? <span className="spinner" /> : null}
                {loading ? 'Inloggen...' : 'Inloggen'}
              </button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-6">
              Nog geen account?{' '}
              <Link to="/register" className="font-medium" style={{ color: 'var(--brand)' }}>
                Registreer hier
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}